import React, { useState, useEffect, useMemo } from "react";
import {
  Search,
  X,
  FileText,
  Phone,
  Tag,
  RotateCcw,
  Filter as FilterIcon,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import SearchableSelect from "./ui/SearchableSelect";
import ThemeDateInput, { CAMPAIGN_THEME } from "./ui/ThemeDateInput";

function formatPhoneAccountLabel(account) {
  const name = account.phoneName || account.wabaName || "Account";
  const number = account.phoneNumber || "";
  return number ? `${name} · ${number}` : name;
}

const STATUS_OPTIONS = [
  { value: "active", label: "Active", color: "#22c55e" },
  { value: "completed", label: "Completed", color: "#6b7280" },
  { value: "scheduled", label: "Scheduled", color: "#3b82f6" },
  { value: "cancelled", label: "Cancelled", color: "#ef4444" },
  { value: "aborted", label: "Aborted", color: "#f97316" },
  { value: "pending", label: "Pending", color: "#eab308" },
  { value: "paused", label: "Paused", color: "#a855f7" },
];

function FilterField({ icon: Icon, label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-2">
        {Icon && <Icon size={12} className="text-gray-400 shrink-0" />}
        {label}
      </label>
      {children}
    </div>
  );
}

const formatStatusOptionLabel = (option) => (
  <span className="flex items-center gap-2">
    <span
      className="w-2 h-2 rounded-full shrink-0"
      style={{ backgroundColor: option.color || "#9ca3af" }}
    />
    <span>{option.label}</span>
  </span>
);

export default function CampaignFilter({
  filters,
  onFilterChange,
  onApplyFilter,
  onClose,
  templates = [],
  phoneAccounts = [],
  loadingPhoneAccounts = false,
  loadingTemplates = false,
  onWhatsappAccountChange,
}) {
  const [localFilters, setLocalFilters] = useState({
    status: filters.status || "",
    campaign_name: filters.campaign_name || "",
    template_id: filters.template_id || "",
    whatsapp_account_id: filters.whatsapp_account_id || "",
    date_from: filters.date_from || "",
    date_to: filters.date_to || "",
  });

  useEffect(() => {
    setLocalFilters({
      status: filters.status || "",
      campaign_name: filters.campaign_name || "",
      template_id: filters.template_id || "",
      whatsapp_account_id: filters.whatsapp_account_id || "",
      date_from: filters.date_from || "",
      date_to: filters.date_to || "",
    });
  }, [filters]);

  const handleInputChange = (field, value) => {
    setLocalFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleWhatsappAccountChange = (value) => {
    setLocalFilters((prev) => ({
      ...prev,
      whatsapp_account_id: value,
      template_id: "",
    }));
    onWhatsappAccountChange?.(value || null);
  };

  const handleApplyFilter = () => {
    onFilterChange(localFilters);
    onApplyFilter(localFilters);
    onClose?.();
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      status: "",
      campaign_name: "",
      template_id: "",
      whatsapp_account_id: "",
      date_from: "",
      date_to: "",
    };
    setLocalFilters(clearedFilters);
    onFilterChange(clearedFilters);
    onApplyFilter(clearedFilters);
    onWhatsappAccountChange?.(null);
  };

  const hasActiveFilters = Object.values(localFilters).some(
    (value) => value !== "" && value !== null
  );

  const activeFilterCount = Object.values(localFilters).filter(
    (v) => v !== "" && v !== null
  ).length;

  const phoneOptions = useMemo(
    () =>
      phoneAccounts.map((account) => ({
        value: String(account.phoneId),
        label: formatPhoneAccountLabel(account),
        searchText: `${account.phoneName || ""} ${account.phoneNumber || ""} ${account.wabaName || ""}`.toLowerCase(),
      })),
    [phoneAccounts]
  );

  const selectedPhoneOption = useMemo(
    () => phoneOptions.find((o) => o.value === String(localFilters.whatsapp_account_id)) || null,
    [phoneOptions, localFilters.whatsapp_account_id]
  );

  const templateOptions = useMemo(
    () =>
      templates.map((template) => ({
        value: String(template.id),
        label: template.name,
        searchText: template.name.toLowerCase(),
      })),
    [templates]
  );

  const selectedTemplateOption = useMemo(
    () => templateOptions.find((o) => o.value === String(localFilters.template_id)) || null,
    [templateOptions, localFilters.template_id]
  );

  const selectedStatusOption = useMemo(
    () => STATUS_OPTIONS.find((o) => o.value === localFilters.status) || null,
    [localFilters.status]
  );

  return (
    <div className="rounded-2xl border border-gray-200 shadow-md overflow-hidden bg-white">
      {/* Header */}
      <div
        className="px-5 py-4 border-b border-gray-200"
        style={{ backgroundColor: CAMPAIGN_THEME.surface }}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="flex items-center justify-center w-10 h-10 rounded-xl border border-gray-200 shadow-sm shrink-0"
              style={{ backgroundColor: CAMPAIGN_THEME.accent }}
            >
              <FilterIcon size={18} className="text-gray-900" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-bold text-gray-900">Filter campaigns</h3>
              <p className="text-xs text-gray-600 mt-0.5">
                Narrow results by account, template, status, or date
              </p>
            </div>
            {hasActiveFilters && (
              <span
                className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold text-gray-900 border border-gray-300 shrink-0"
                style={{ backgroundColor: CAMPAIGN_THEME.accent }}
              >
                {activeFilterCount} active
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-900 hover:bg-white/80 rounded-xl border border-transparent hover:border-gray-200 transition-all shrink-0"
            title="Close filters"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="p-5 space-y-5">
        {/* Search */}
        <section>
          <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-3 flex items-center gap-1.5">
            <Search size={12} />
            Search
          </p>
          <FilterField icon={FileText} label="Campaign name">
            <div className="relative">
              <input
                type="text"
                value={localFilters.campaign_name}
                onChange={(e) => handleInputChange("campaign_name", e.target.value)}
                placeholder="Type to search by campaign name…"
                className="w-full pl-10 pr-10 py-2.5 text-sm font-medium border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[rgba(17,24,39,0.08)] focus:border-gray-900 transition-all placeholder:text-gray-400 bg-white shadow-sm"
              />
              <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              {localFilters.campaign_name && (
                <button
                  type="button"
                  onClick={() => handleInputChange("campaign_name", "")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X size={14} className="text-gray-400 hover:text-gray-700" />
                </button>
              )}
            </div>
          </FilterField>
        </section>

        {/* Criteria */}
        <section>
          <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-3 flex items-center gap-1.5">
            <Sparkles size={12} />
            Criteria
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <FilterField icon={Tag} label="Status">
              <SearchableSelect
                variant="campaign"
                options={STATUS_OPTIONS}
                value={selectedStatusOption}
                onChange={(opt) => handleInputChange("status", opt?.value || "")}
                placeholder="All statuses"
                isClearable
                formatOptionLabel={formatStatusOptionLabel}
                noOptionsMessage={() => "No statuses found"}
              />
            </FilterField>

            <FilterField icon={FileText} label="Template">
              <SearchableSelect
                variant="campaign"
                options={templateOptions}
                value={selectedTemplateOption}
                onChange={(opt) => handleInputChange("template_id", opt?.value || "")}
                placeholder={loadingTemplates ? "Loading templates…" : "All templates"}
                isLoading={loadingTemplates}
                isDisabled={loadingTemplates}
                isClearable
                noOptionsMessage={() =>
                  localFilters.whatsapp_account_id
                    ? "No templates for this account"
                    : "No templates found"
                }
              />
            </FilterField>

            <FilterField icon={Phone} label="WhatsApp account">
              <SearchableSelect
                variant="campaign"
                options={phoneOptions}
                value={selectedPhoneOption}
                onChange={(opt) => handleWhatsappAccountChange(opt?.value || "")}
                placeholder={loadingPhoneAccounts ? "Loading accounts…" : "All accounts"}
                isLoading={loadingPhoneAccounts}
                isDisabled={loadingPhoneAccounts}
                isClearable
                noOptionsMessage={() => "No accounts found"}
              />
            </FilterField>
          </div>
        </section>

        {/* Date range */}
        <section
          className="rounded-2xl border border-gray-200 p-4 shadow-sm"
          style={{ backgroundColor: CAMPAIGN_THEME.surface }}
        >
          <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-3">
            Date range
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-3 items-center">
            <FilterField label="From">
              <ThemeDateInput
                value={localFilters.date_from}
                onChange={(v) => handleInputChange("date_from", v)}
                placeholder="Start date"
                max={localFilters.date_to || undefined}
              />
            </FilterField>

            <div className="hidden sm:flex items-center justify-center self-end pb-1">
              <span
                className="flex items-center justify-center w-8 h-8 rounded-full border border-gray-300 bg-white text-gray-500 shadow-sm"
              >
                <ArrowRight size={14} />
              </span>
            </div>

            <FilterField label="To">
              <ThemeDateInput
                value={localFilters.date_to}
                onChange={(v) => handleInputChange("date_to", v)}
                placeholder="End date"
                min={localFilters.date_from || undefined}
              />
            </FilterField>
          </div>
          <p className="text-[11px] text-gray-500 mt-3 leading-relaxed">
            Filters campaigns by creation date. Pick a start and end date, or use either field alone.
          </p>
        </section>
      </div>

      {/* Footer */}
      <div
        className="px-5 py-4 border-t border-gray-200"
        style={{ backgroundColor: CAMPAIGN_THEME.surface }}
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {hasActiveFilters ? (
              <>
                <span className="flex items-center gap-2 text-xs font-semibold text-gray-700">
                  <span
                    className="w-2 h-2 rounded-full animate-pulse"
                    style={{ backgroundColor: CAMPAIGN_THEME.accent }}
                  />
                  {activeFilterCount} filter{activeFilterCount !== 1 ? "s" : ""} active
                </span>
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                >
                  <RotateCcw size={12} />
                  Reset all
                </button>
              </>
            ) : (
              <p className="text-xs text-gray-500 font-medium">No filters applied yet</p>
            )}
          </div>
          <button
            type="button"
            onClick={handleApplyFilter}
            className="inline-flex items-center justify-center gap-2 text-gray-900 px-5 py-2.5 rounded-xl font-bold text-sm transition-all shadow-sm hover:shadow-md border border-gray-300 w-full sm:w-auto"
            style={{ backgroundColor: CAMPAIGN_THEME.accent }}
          >
            <Search size={15} />
            Apply filters
          </button>
        </div>
      </div>
    </div>
  );
}
